import React, { useState } from 'react';
import { ArrowLeftIcon, CheckIcon, EyeIcon, ExternalLinkIcon, EyeOffIcon, HandIcon, LinkIcon, LockIcon, PencilIcon } from 'lucide-react';
import { InitialAvatar } from '../../components/InitialAvatar';
import { Button } from '../../components/ui/Button';
import { SectionCard } from '../../components/ui/SectionCard';
import { YearsRibbon } from '../../components/profile/YearsRibbon';
import { BadgeMark, MemberBadges } from '../../components/profile/MemberBadges';
import { badgesFor } from '../../data/badges';
import { ArchiveTile } from '../../components/archive/ArchiveTile';
import { ArchiveDetail } from '../../components/archive/ArchiveDetail';
import { PrototypeNote } from '../../components/prototype/PrototypeNote';
import { prototypeStateFor } from '../../data/prototypeStates';
import { AFFILIATION_LABELS, CURRENT_MEMBER, overlapWith, publicName } from '../../data/community';
import type { Member } from '../../data/community';
import { ARCHIVE_ITEMS } from '../../data/archive';
import type { ArchiveItem } from '../../data/archive';
interface ProfileProps {
  member: Member;
  onBack: () => void;
}
export function Profile({
  member,
  onBack
}: ProfileProps) {
  const isOwn = member.name === CURRENT_MEMBER.fullName;
  const [visibility, setVisibility] = useState(member.visibility ?? 'public');
  const [waved, setWaved] = useState(false);
  const [openItem, setOpenItem] = useState<ArchiveItem | null>(null);
  const overlap = isOwn ? null : overlapWith(member, CURRENT_MEMBER);
  const contributions = ARCHIVE_ITEMS.filter(item => item.credit.includes(publicName(member.name)));
  return <div className="min-h-full bg-canvas" data-id="element-781" data-mp="3bwnhb9nxg8hn">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 px-4 py-6 sm:px-6" data-id="element-782" data-mp="3gtnmlicyd77p">
        <button type="button" onClick={onBack} className="inline-flex min-h-tap w-fit items-center gap-2 rounded-control text-small text-ink-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-783" data-mp="32u4kp7aqis3u">
          <ArrowLeftIcon aria-hidden="true" className="h-4 w-4" data-id="element-784" data-mp="18mk6bpz3n39t" />
          Back to people
        </button>

        {/* IDENTITY */}
        <SectionCard data-id="element-785" data-mp="13nz5ppfxqhku">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between" data-id="element-786" data-mp="13ngyxyvnckyo">
            <div className="flex items-start gap-4" data-id="element-787" data-mp="ypxnf6qc2maf">
              <InitialAvatar name={member.name} size={72} data-id="element-788" data-mp="1wcegu0vy4fqw" />
              <div className="flex flex-col gap-1" data-id="element-789" data-mp="1w9s9o2cyw297">
                <h1 className="flex items-center gap-2 text-page-title font-semibold text-ink" data-id="element-790" data-mp="1rdthe3oiq03i">
                  {publicName(member.name)}
                  <BadgeMark name={member.name} className="h-6 w-6" data-id="element-791" data-mp="1mfrzvn6qq8u5" />
                </h1>
                <p className="text-body text-ink-muted" data-id="element-792" data-mp="1ibtzl95oirzi">
                  {member.years} · {AFFILIATION_LABELS[member.affiliation]}
                  {member.city ? ` · ${member.city}` : ''}
                </p>
                {member.joined ? <p className="text-small text-ink-muted" data-id="element-793" data-mp="2iktp1gzdo2nb">
                    {isOwn ? 'You joined' : 'Joined'} {member.joined.toLowerCase()}
                  </p> : null}
                {prototypeStateFor(member.name) ? <PrototypeNote variant="block" className="mt-2" data-id="element-794" data-mp="2rjf0ytacgjf4">
                    {prototypeStateFor(member.name)!.note}
                  </PrototypeNote> : null}
              </div>
            </div>

            <div className="flex shrink-0 flex-wrap gap-2" data-id="element-795" data-mp="31d4bk09q5nz9">
              {isOwn ? <Button variant="secondary" data-id="element-796" data-mp="3681ld9etw1d7">
                  <PencilIcon aria-hidden="true" className="h-4 w-4" data-id="element-797" data-mp="3aay89cbeijmy" />
                  Edit profile
                </Button> : <Button onClick={() => setWaved(true)} disabled={waved} data-id="element-798" data-mp="3f9rkl1eyc4lj">
                  {waved ? <>
                      <CheckIcon aria-hidden="true" className="h-4 w-4" data-id="element-799" data-mp="1kwjn11zldew7" />
                      Waved
                    </> : <>
                      <HandIcon aria-hidden="true" className="h-4 w-4" data-id="element-800" data-mp="pkih9av7vcm0" />
                      Wave
                    </>}
                </Button>}
            </div>
          </div>

          {waved && !isOwn ? <p className="mt-4 rounded-field bg-primary-soft px-4 py-3 text-small text-on-primary-soft" data-id="element-801" data-mp="ukwq2v4n6ql3">
              {publicName(member.name)} will see that you waved. There&apos;s nothing to answer
              and no reply expected.
            </p> : null}
        </SectionCard>

        {/* YEARS */}
        <SectionCard title={isOwn ? 'Your years' : 'Their years'} meta={overlap ? `You were both here ${overlap.start}–${overlap.end}` : !isOwn ? 'Your years don’t overlap' : undefined} data-id="element-802" data-mp="gmq9crrtr78n">
          <YearsRibbon member={member} compare={!isOwn} data-id="element-803" data-mp="3fam3d3pp64bo" />
          {!isOwn ? <p className="mt-4 text-small text-ink-muted" data-id="element-804" data-mp="1ut4h7kdczpex">
              The lower bar is you, {CURRENT_MEMBER.years}.
            </p> : null}
        </SectionCard>

        {/* WHAT THEY WERE PART OF */}
        {member.programs?.length || member.about ? <SectionCard title={isOwn ? 'What you were part of' : 'What they were part of'} data-id="element-805" data-mp="1ktlzzbou2hqk">
            {member.programs?.length ? <ul className="flex flex-wrap gap-2" data-id="element-806" data-mp="2djuk392xjc7s">
                {member.programs.map(program => <li key={program} className="rounded-pill border border-rule px-3.5 py-1.5 text-small text-ink" data-id="element-807" data-mp="2ii5upm11tqr2">
                    {program}
                  </li>)}
              </ul> : null}

            {member.about ? <p className="mt-4 max-w-2xl text-body text-ink" data-id="element-808" data-mp="t3hfze4zjwpv">{member.about}</p> : null}
          </SectionCard> : null}

        {/* RECOGNITION */}
        {badgesFor(member.name).length > 0 ? <SectionCard title={isOwn ? 'What you’ve done here' : 'What they’ve done here'} data-id="element-809" data-mp="2zrrkqwe90ygz">
            <MemberBadges name={member.name} own={isOwn} data-id="element-810" data-mp="2uu89m5cul1t9" />
          </SectionCard> : null}

        {/* LINKS — optional, member-supplied, members-only. */}
        {member.links?.length || isOwn ? <SectionCard title="Elsewhere" meta={isOwn ? 'Shown only to members you’ve both waved at' : member.wavedBack ? 'Unlocked — you’ve both waved' : 'Locked'} data-id="element-811" data-mp="2l1kdzdxlt3w4">
            {!isOwn && !member.wavedBack ? <div className="flex flex-col gap-3" data-id="element-812" data-mp="225aq5ywwgyyx">
                <p className="flex items-start gap-2.5 text-small text-ink-muted" data-id="element-813" data-mp="226lxg8pk0ous">
                  <LockIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-814" data-mp="5bz7u4rkh8j3" />
                  {publicName(member.name)} has added links. You&apos;ll see them once you have
                  both waved — they see yours at the same moment, and either of you can undo it.
                </p>
                {waved ? <p className="text-small text-ink-muted" data-id="element-815" data-mp="hn9zc0jx9s63">
                    You&apos;ve waved. Nothing more to do — it&apos;s up to them now.
                  </p> : <Button variant="secondary" onClick={() => setWaved(true)} data-id="element-816" data-mp="3m29r5jszlkt7">
                    <HandIcon aria-hidden="true" className="h-4 w-4" data-id="element-817" data-mp="3q1s3vsanmpky" />
                    Wave at {member.name.split(' ')[0]}
                  </Button>}
              </div> : member.links?.length ? <ul className="flex flex-wrap gap-2" data-id="element-818" data-mp="3wwrrzay0fha">
                {member.links.map(link => <li key={link.url} data-id="element-819" data-mp="1aaw6fisczexp">
                    <a href={`https://${link.url}`} target="_blank" rel="noreferrer noopener" className="inline-flex min-h-tap items-center gap-2 rounded-pill border border-rule px-4 text-small text-ink hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-820" data-mp="1faiivq0d4r5g">
                      <LinkIcon aria-hidden="true" className="h-4 w-4 text-ink-muted" data-id="element-821" data-mp="25cl0hceybs4u" />
                      {link.label}
                      <ExternalLinkIcon aria-hidden="true" className="h-3.5 w-3.5 text-ink-subtle" data-id="element-822" data-mp="2k0z59svxhqo1" />
                    </a>
                  </li>)}
              </ul> : <p className="text-small text-ink-muted" data-id="element-823" data-mp="2t2ovive2qjk8">
                You haven&apos;t added any. Optional — and only ever shown to a member after the
                two of you have both waved.
              </p>}
            {isOwn ? <Button variant="secondary" className="mt-4" data-id="element-824" data-mp="tqmcfm9d3r73">
                <PencilIcon aria-hidden="true" className="h-4 w-4" data-id="element-825" data-mp="ts79pjn29o8n" />
                {member.links?.length ? 'Edit links' : 'Add a link'}
              </Button> : null}
          </SectionCard> : null}

        {/* CONTRIBUTIONS */}
        <SectionCard title={isOwn ? 'What you’ve added' : 'What they’ve added'} meta={`${contributions.length} ${contributions.length === 1 ? 'item' : 'items'} in the archive`} data-id="element-826" data-mp="2wlxoiyfx7wzl">
          {contributions.length > 0 ? <ul className="columns-1 gap-4 sm:columns-2 [&>li]:mb-4" data-id="element-827" data-mp="2rlrtd8afzscr">
              {contributions.map(item => <ArchiveTile key={item.id} item={item} onOpen={setOpenItem} data-id="element-828" data-mp="2dn1vvrjpnqnv" />)}
            </ul> : <p className="max-w-xl text-small text-ink-muted" data-id="element-829" data-mp="2hon592x1dmtb">
              {isOwn ? 'Nothing yet, and that’s a perfectly ordinary way to be here. The years you were around are thin in the archive if you ever want to change that.' : 'Nothing in the archive from them yet.'}
            </p>}
        </SectionCard>

        {/* VISIBILITY — own profile only */}
        {isOwn ? <SectionCard title="How you appear" data-id="element-830" data-mp="2dqg1lfjnt20e">
            <p className="max-w-xl text-small text-ink-muted" data-id="element-831" data-mp="1z08gst66j6wj">
              Change this whenever you like. Nothing you&apos;ve added is deleted when you go
              private — it stops being attributed to a visible profile.
            </p>

            <div className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-832" data-mp="2roxiouf27g9c">
              {[{
            value: 'public' as const,
            icon: EyeIcon,
            title: 'Visible to members',
            body: `Signed-in members can see you as ${CURRENT_MEMBER.publicName}, your years, and what you were part of.`
          }, {
            value: 'private' as const,
            icon: EyeOffIcon,
            title: 'Not listed',
            body: 'You can read everything and nobody can see that you’re here. Plenty of members stay this way.'
          }].map(option => {
            const selected = visibility === option.value;
            return <button key={option.value} type="button" role="radio" aria-checked={selected} onClick={() => setVisibility(option.value)} className={`flex flex-col gap-2 rounded-field border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${selected ? 'border-primary bg-primary-soft' : 'border-rule hover:bg-canvas'}`} data-id="element-833" data-mp="18pgexn19zcjp">
                    <span className="flex items-center gap-2" data-id="element-834" data-mp="3tfp0ekm6i0b4">
                      <option.icon aria-hidden="true" className={`h-4 w-4 ${selected ? 'text-primary-text' : 'text-ink-muted'}`} data-id="element-835" data-mp="3tf5slwq8ssat" />
                      <span className={`text-body font-medium ${selected ? 'text-primary-text' : 'text-ink'}`} data-id="element-836" data-mp="qy7f1a9losdn">
                        {option.title}
                      </span>
                      {selected ? <CheckIcon aria-hidden="true" className="ml-auto h-4 w-4 text-primary-text" data-id="element-837" data-mp="41nkggda5t0j" /> : null}
                    </span>
                    <span className="text-small text-ink-muted" data-id="element-838" data-mp="hzvgmm2hsqan">{option.body}</span>
                  </button>;
          })}
            </div>

            <ul className="mt-4 flex flex-col gap-2 rounded-field bg-canvas p-4" data-id="element-839" data-mp="1ohiiyo2o9hxv">
              {['Your email and phone are never shown to another member, on any setting.', 'Your full last name is never shown — members see Ray M.', 'Anything you contribute is read by a staff member before it appears.'].map(line => <li key={line} className="flex items-start gap-2.5 text-small text-ink-muted" data-id="element-840" data-mp="344xbnkykwsok">
                  <LockIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-841" data-mp="2z7dk5jcvejeo" />
                  {line}
                </li>)}
            </ul>
          </SectionCard> : <SectionCard data-id="element-842" data-mp="2l8xd1rb3iqw0">
            <p className="flex items-start gap-2.5 text-small text-ink-muted" data-id="element-843" data-mp="2l84h7ldodlsn">
              <LockIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-844" data-mp="2ga2xqzcc7kgn" />
              Contact details are never shown to members. If you want to reach{' '}
              {publicName(member.name)}, wave — they&apos;ll decide whether to write back.
            </p>
          </SectionCard>}
      </div>

      <ArchiveDetail item={openItem} onClose={() => setOpenItem(null)} data-id="element-845" data-mp="mg18p62grl6e" />
    </div>;
}